import clsx from 'clsx';
import { MouseEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AssignmentFeedback } from 'src/components/assignments/assignment-feedback';
import { AssignmentImage } from 'src/components/assignments/assignment-image';
import { AssignmentStatusBadge } from 'src/components/assignments/assignment-status';
import { Subtitle } from 'src/components/assignments/stream-card-content/subtitle';
import { AmplifyButton } from 'src/components/common/amplify-button';
import { EmptySlotDashIcon } from 'src/components/icons';
import { Tooltip } from 'src/components/tooltip';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useFeedback } from 'src/hooks/use-feedback.hook';
import { useFormatAssignmentDueDate } from 'src/hooks/use-format-assignment-due-date';
import { useScore } from 'src/hooks/use-score.hook';
import { useTrimmedElementCheck } from 'src/hooks/use-trimmed-element-check';
import {
  DesmosAssignment,
  SingleAssignment,
} from 'src/interfaces/assignment.types';
import { TaughtLessonAssignment } from 'src/interfaces/class-stats.types';
import { getAmpConfigFlags } from 'src/services/amp-config.service';
import { useActiveMSAssignmentsStatus } from 'src/store/features';
import {
  getAssignmentLessonTitle,
  getMSAssignmentStatusOption,
  getShouldShowOpenButton,
  isDesmosAssignmentContent,
} from 'src/utils/assignments.utils';

type MiddleSchoolCardContentProps = {
  assignment: SingleAssignment | TaughtLessonAssignment | DesmosAssignment;
  thumbnailPath?: string;
  isThumbnailDistorted?: boolean;
  isFullyCustom?: boolean;
  isCustomized?: boolean;
  domain?: string;
  title?: string;
  isTaughtLesson?: boolean;
  dueDate?: number | null;
  isOverdue?: boolean;
  isStatusChangeDialogVisible?: boolean;
  onUndoButtonClick?: (e: MouseEvent<HTMLElement>) => void;
  msButtonHref?: string;
  onMsButtonClick?: (e: MouseEvent<HTMLElement>) => void;
};

export const MiddleSchoolCardContent = ({
  assignment,
  thumbnailPath,
  isFullyCustom,
  isCustomized,
  domain,
  title,
  msButtonHref,
  onMsButtonClick,
  isThumbnailDistorted,
}: MiddleSchoolCardContentProps) => {
  const { t } = useTranslation();

  const formatAssignmentDueDate = useFormatAssignmentDueDate();
  const activeAssignmentsFilter = useActiveMSAssignmentsStatus();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const { feedback } = useFeedback(assignment.contentBk);
  const { score } = useScore(assignment.contentBk);

  const assignmentNameRef = useRef<HTMLHeadingElement>(null);

  const isAssignmentNameTrimmed = useTrimmedElementCheck(assignmentNameRef);

  const { content, isHandedInmCLASSAssessment } = assignment;
  const unitTitle = 'unitTitle' in content ? content.unitTitle : undefined;
  const subtitle = 'subtitle' in content ? content.subtitle : undefined;

  // show start/open buttons based on assignment status if 'All' filter option is active
  const shouldShowOpenMsButton = getShouldShowOpenButton({
    activeAssignmentsFilter,
    assignmentStatus: assignment.status,
  });

  const assignmentStatus = getMSAssignmentStatusOption(assignment.status);

  const isDesmos = isDesmosAssignmentContent(content);

  const assignmentNameLabel =
    isCustomized || isDesmos ? title : getAssignmentLessonTitle(title);

  // TODO: Remove the feature flag after RFS.
  const isUpdatedMSViewEnabled = getAmpConfigFlags().isUpdatedMSViewEnabled;

  return (
    <>
      <AssignmentImage
        distorted={isThumbnailDistorted}
        imagePath={thumbnailPath}
        locked={isHandedInmCLASSAssessment}
      />
      <div
        className={clsx(
          'assignment__info-container assignment__info-container--middle-school',
          {
            'assignment__info-container--full-custom': isFullyCustom,
          },
        )}
      >
        <div className="assignment__main-caption">
          {!isFullyCustom && (
            <Subtitle
              assignment={assignment}
              subtitle={subtitle}
              assignmentDomain={domain}
              unitTitle={unitTitle}
              assignmentTitle={title}
            />
          )}
          <Tooltip
            title={assignmentNameLabel ?? ''}
            classNames={{ tooltip: 'sh-mobile-tooltip-bottom-left' }}
            suppress={!isAssignmentNameTrimmed}
          >
            <h2
              ref={assignmentNameRef}
              className="assignment__name assignment__name--higher-grade assignment__name--middle-school"
              tabIndex={isAssignmentNameTrimmed ? 0 : -1}
            >
              {assignmentNameLabel}
            </h2>
          </Tooltip>
        </div>
        <AssignmentStatusBadge assignmentStatus={assignment.status} />
        <AssignmentFeedback
          feedback={feedback}
          score={score}
          assignment={assignment}
          commonXapiContextPayload={commonXapiContextPayload}
          assignmentStatus={assignmentStatus}
        />
        <div className="assignment__date-container assignment__date-container--middle-school">
          {assignment.type !== 'TaughtLessonAssignment' &&
          assignment.enrollmentType !== 'single_session' &&
          assignment.dueDate ? (
            <p className="assignment__date assignment__date--middle-school">
              {formatAssignmentDueDate(new Date(assignment.dueDate), true)}
            </p>
          ) : (
            <EmptySlotDashIcon />
          )}
        </div>
        <div className="assignment__ms-button">
          {shouldShowOpenMsButton ? (
            <AmplifyButton
              href={msButtonHref}
              onClick={onMsButtonClick}
              onContextMenu={onMsButtonClick}
              target="_self"
              variant="secondary"
              ariaLabel="Open lesson"
              disabled={isHandedInmCLASSAssessment}
            >
              {t('Open')}
            </AmplifyButton>
          ) : (
            <AmplifyButton
              href={msButtonHref}
              onClick={onMsButtonClick}
              onContextMenu={onMsButtonClick}
              target="_self"
              ariaLabel="Start lesson"
              disabled={isHandedInmCLASSAssessment}
              variant={isUpdatedMSViewEnabled ? 'blue' : 'primary'}
            >
              {t('Start')}
            </AmplifyButton>
          )}
        </div>
      </div>
    </>
  );
};
